import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import api from 'api';
import { Button, ModalButtonWithForm } from 'components/common';
import {
  AccessAndVisibility,
  AccessAndVisibilityCriteria
} from 'components/common/accessAndVisibility';
import { Box } from 'components/common/box';
import {
  Checkbox,
  DisplayField,
  Form,
  FormGroup,
  FormSection,
  NumberStepper
} from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';

import type { FormApi } from 'informed';
import type { CalendarEvent } from 'shared-types';
import type { AccessAndVisibilityFormElements } from 'components/common/accessAndVisibility/AccessAndVisibility';

type FormState = AccessAndVisibilityFormElements<
  'groups' | 'sso' | 'userAttributes'
> &
  Pick<CalendarEvent, 'queueEnabled' | 'queueLimit'> & {
    addOptionalPublicAttributes?: boolean;
  };

const CalendarEventAccessAndVisibility = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { calendarEvent },
    serverConfig: {
      calendarEventDefaultParticipantData,
      calendarEnablePublicCalendar
    },
    theme
  } = useReduxState();

  const formApi = useRef<FormApi<FormState>>(undefined);

  const [loading, setLoading] = useState(false);

  const confirmPublicEventAttributes = Boolean(
    calendarEnablePublicCalendar &&
      calendarEventDefaultParticipantData?.some(
        (attribute) => attribute.publicEventsOnly
      )
  );

  const handleConfirmPublicAttributes = async (values: {
    addOptionalPublicAttributesInner?: string;
  }) => {
    if (values.addOptionalPublicAttributesInner) {
      formApi.current.setValue('addOptionalPublicAttributes', true);
    }

    await handleSubmit(formApi.current.getState().values);

    return true;
  };

  const handleSubmit = async (values: FormState) => {
    setLoading(true);

    const { addOptionalPublicAttributes, ...otherValues } = values;

    try {
      const newEvent = {
        ...calendarEvent,
        ...otherValues,
        access: (values.visibility === 'public'
          ? 'public'
          : values.visibility === 'private'
          ? 'added'
          : values.restrictions === 'request-access'
          ? 'request-access'
          : 'open') as CalendarEvent['access'],
        visibilityCriteria: {
          sso: values.visibilityCriteria.sso,
          groups: values.visibilityCriteria.groups,
          userAttributes: values.visibilityCriteria.userAttributes
        }
      };

      await api.put(`/admin/calendar-events/${calendarEvent.id}`, {
        ...newEvent,
        addOptionalPublicAttributes
      });

      dispatch.updatePageState({
        calendarEvent: newEvent,
        hasUnsavedChanges: false,
        refreshInitialState: true
      });
      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  const initialValues: FormState = {
    addOptionalPublicAttributes: false,
    participationLimit: calendarEvent.participationLimit || 0,
    restrictions:
      calendarEvent.access === 'request-access'
        ? 'request-access'
        : calendarEvent.participationLimit > 0
        ? 'user-limit'
        : 'none',
    visibility: calendarEvent.visibility,
    visibilityCriteria: {
      groups: calendarEvent.visibilityCriteria?.groups || false,
      sso: calendarEvent.visibilityCriteria?.sso || false,
      userAttributes: calendarEvent.visibilityCriteria?.userAttributes || false
    },
    queueEnabled: calendarEvent.queueEnabled,
    queueLimit: calendarEvent.queueLimit
  };

  return (
    <div>
      <Box padding={0} title={messages.eventCalendarSettingsAccess}>
        <Form
          initialValues={initialValues}
          onSubmit={handleSubmit}
          getApi={(form) => (formApi.current = form)}
        >
          {({ formState }) => (
            <>
              <FormSection maxWidth="100%">
                <UnsavedFormChanges initialValues={initialValues} />

                <AccessAndVisibility
                  customLabels={{
                    headerUserAccess: messages.eventCalendarAvailable,
                    headerUserAccessHelpText: getMessageWithPlaceholders({
                      message: messages.eventCalendarAvailableHelpText,
                      placeholders: {
                        participantsPage: messages.commonParticipants
                      }
                    }),
                    headerUserLimits:
                      messages.eventCalendarNumberOfParticipants,
                    restrictionOptionUserLimit:
                      messages.eventCalendarAvailableRestrictionUserLimit,
                    restrictionOptionUserRequest:
                      messages.eventCalendarAvailableRestrictionUserRequest
                  }}
                  hasPendingRequests={
                    calendarEvent.numberOfPendingAccessRequests > 0
                  }
                  visibility={{
                    anyone: true,
                    limited: true,
                    private: true,
                    public: calendarEnablePublicCalendar
                  }}
                />

                {formState?.values?.restrictions === 'user-limit' ? (
                  <FormGroup
                    label={messages.eventCalendarSettingsQueue}
                    labelHelpText={messages.eventCalendarSettingsQueueInfo}
                  >
                    <Checkbox
                      id="queue-enabled"
                      field="queueEnabled"
                      label={messages.eventCalendarSettingsQueueEnable}
                      style={{ marginBottom: 10 }}
                    />

                    <NumberStepper
                      disabled={!formState?.values?.queueEnabled}
                      id="queue-limit"
                      field="queueLimit"
                      label={messages.eventCalendarSettingsQueueLimit}
                      max={100000}
                      min={1}
                      precision={1}
                      stepValue={1}
                    />
                  </FormGroup>
                ) : null}
              </FormSection>

              <DisplayField field="addOptionalPublicAttributes" />

              <FormSection>
                {formState.values.visibility === 'public' &&
                calendarEvent.visibility !== 'public' &&
                confirmPublicEventAttributes ? (
                  <ModalButtonWithForm
                    buttonCategory="success"
                    buttonText={messages.commonSave}
                    modalActionText={messages.commonSave}
                    modalTitle={messages.commonSaveChanges}
                    onSubmit={handleConfirmPublicAttributes}
                    preventSubmit={true}
                    stopPropagation={true}
                  >
                    {() => (
                      <div className="confirm-public-attributes">
                        {calendarEventDefaultParticipantData.some(
                          (attribute) =>
                            attribute.publicEventsOnly &&
                            !attribute.publicEventsOnlyOptional
                        ) ? (
                          <FormGroup
                            label={messages.commonCustomFields}
                            style={{ marginBottom: 25 }}
                          >
                            <div className="confirm-public-attributes-label">
                              {
                                messages.eventCalendarParticipantDataFieldsDefault
                              }
                            </div>

                            <table className="confirm-public-attributes-table">
                              {calendarEventDefaultParticipantData
                                .filter(
                                  (attribute) =>
                                    attribute.publicEventsOnly &&
                                    !attribute.publicEventsOnlyOptional
                                )
                                .map((attribute, attributeIndex) => (
                                  <tr
                                    className="confirm-public-attributes-row"
                                    key={attributeIndex}
                                  >
                                    <td className="confirm-public-attributes-cell">
                                      {attribute.title}
                                    </td>
                                  </tr>
                                ))}
                            </table>
                          </FormGroup>
                        ) : null}

                        {calendarEventDefaultParticipantData.some(
                          (attribute) =>
                            attribute.publicEventsOnly &&
                            attribute.publicEventsOnlyOptional
                        ) ? (
                          <FormGroup
                            label={
                              messages.eventCalendarParticipantDataFieldsOptional
                            }
                            style={{ marginBottom: 25 }}
                          >
                            <Checkbox
                              field="addOptionalPublicAttributesInner"
                              label={
                                messages.eventCalendarParticipantDataFieldsOptionalLabel
                              }
                            />

                            <table className="confirm-public-attributes-table">
                              {calendarEventDefaultParticipantData
                                .filter(
                                  (attribute) =>
                                    attribute.publicEventsOnly &&
                                    attribute.publicEventsOnlyOptional
                                )
                                .map((attribute, attributeIndex) => (
                                  <tr
                                    className="confirm-public-attributes-row"
                                    key={attributeIndex}
                                  >
                                    <td className="confirm-public-attributes-cell">
                                      {attribute.title}
                                    </td>
                                  </tr>
                                ))}
                            </table>
                          </FormGroup>
                        ) : null}
                      </div>
                    )}
                  </ModalButtonWithForm>
                ) : (
                  <Button
                    category="success"
                    disabled={formState.pristine}
                    loading={loading}
                    text={messages.commonSave}
                    type="submit"
                  />
                )}
              </FormSection>
            </>
          )}
        </Form>
      </Box>

      <div style={{ marginTop: 40 }}>
        <AccessAndVisibilityCriteria
          isAvailable={{ groups: true, userAttributes: true }}
          isEnabled={{
            groups: calendarEvent?.visibilityCriteria?.groups || false,
            userAttributes:
              calendarEvent?.visibilityCriteria?.userAttributes || false
          }}
          productId={calendarEvent.id}
          type="calendar-event"
        />
      </div>

      <style jsx>{`
        .confirm-public-attributes-label {
          font-weight: 400;
        }
        .confirm-public-attributes-table {
          border-radius: ${theme.inputBorderRadius};
          border-collapse: collapse;
          box-shadow: 0 0 0 1px ${theme.lightGreyDark};
          margin-top: 10px;
          width: 100%;
        }
        .confirm-public-attributes-row ~ .confirm-public-attributes-row {
          border-top: 1px solid ${theme.lightGreyDark};
        }
        .confirm-public-attributes-cell {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
          padding: 7px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventAccessAndVisibility;
